/**
 * Dialog for promoting a canonical composer to a Wikidata reference.
 * Allows admins to enter a Wikidata QID, auto-fetches the name for confirmation,
 * and promotes all linked pieces and suggestions to use the Wikidata reference.
 */

import { useState, useEffect } from 'react'
import { usePromoteCanonicalComposer } from '../../hooks/useCanonicalComposers'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@brent/core-ui/ui'
import { Button } from '@brent/core-ui/ui'
import { Label } from '@brent/core-ui/ui'
import { Input } from '@brent/core-ui/ui'
import { Alert, AlertDescription } from '@brent/core-ui/ui'
import { Loader2, ArrowUpRight, CheckCircle2, AlertCircle } from 'lucide-react'
import { useToast } from '../../hooks/use-toast'
import { getComposerById } from '../../services/api/wikidata'
import type { CanonicalComposer } from '../../types/canonical-composer'

interface PromoteCanonicalComposerDialogProps {
  composer: CanonicalComposer
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function PromoteCanonicalComposerDialog({
  composer,
  open,
  onOpenChange,
}: PromoteCanonicalComposerDialogProps) {
  const promoteMutation = usePromoteCanonicalComposer()
  const { toast } = useToast()

  const [wikidataId, setWikidataId] = useState('')
  const [fetchedName, setFetchedName] = useState<string | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setWikidataId('')
      setFetchedName(null)
      setIsFetching(false)
      setFetchError(null)
    }
  }, [open])

  // Auto-fetch composer name when QID looks valid
  useEffect(() => {
    const trimmedId = wikidataId.trim()
    if (!trimmedId || !trimmedId.match(/^Q\d+$/i)) {
      setFetchedName(null)
      setFetchError(null)
      return
    }

    setIsFetching(true)
    setFetchedName(null)
    setFetchError(null)

    const timeoutId = setTimeout(async () => {
      try {
        const result = await getComposerById(trimmedId)
        setFetchedName(result.name)
        setFetchError(null)
      } catch (error) {
        setFetchedName(null)
        setFetchError(
          error instanceof Error ? error.message : `Could not fetch details for ${trimmedId}`,
        )
      } finally {
        setIsFetching(false)
      }
    }, 500)

    return () => {
      clearTimeout(timeoutId)
      setIsFetching(false)
    }
  }, [wikidataId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const trimmedId = wikidataId.trim()
    if (!trimmedId || !trimmedId.match(/^Q\d+$/i)) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a valid Wikidata QID (e.g. Q255)',
        variant: 'destructive',
      })
      return
    }

    try {
      const result = await promoteMutation.mutateAsync({
        composerId: composer.canonical_composer_id,
        input: {
          wikidata_id: trimmedId,
          wikidata_name: fetchedName ?? undefined,
        },
      })

      toast({
        title: 'Composer promoted',
        description: `Successfully promoted to Wikidata. Updated ${result.promoted_pieces} pieces and ${result.promoted_suggestions} suggestions.`,
      })

      onOpenChange(false)
    } catch (error) {
      toast({
        title: 'Promotion failed',
        description: error instanceof Error ? error.message : 'Failed to promote composer',
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpRight className="h-5 w-5" />
            Promote to Wikidata
          </DialogTitle>
          <DialogDescription>
            Replace this canonical composer with a Wikidata reference. All linked pieces and
            suggestions will be updated.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Alert>
            <AlertDescription>
              <strong>Current composer:</strong> {composer.name}
              {composer.birth_year && ` (${composer.birth_year}–${composer.death_year ?? ''})`}
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="wikidata-qid">Wikidata QID *</Label>
            <div className="relative">
              <Input
                id="wikidata-qid"
                value={wikidataId}
                onChange={(e) => setWikidataId(e.target.value)}
                placeholder="e.g. Q255"
                disabled={promoteMutation.isPending}
              />
              {isFetching && (
                <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Enter the Wikidata QID for this composer. The name will be fetched automatically.
            </p>
          </div>

          {/* Fetched name confirmation */}
          {fetchedName && (
            <div className="flex items-center gap-2 rounded-md border p-3 bg-green-50 dark:bg-green-950">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-sm">
                <strong>Wikidata match:</strong> {fetchedName}
              </span>
            </div>
          )}

          {/* Fetch error */}
          {fetchError && (
            <div className="flex items-center gap-2 rounded-md border p-3 bg-yellow-50 dark:bg-yellow-950">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <span className="text-sm text-yellow-800 dark:text-yellow-200">
                Could not verify QID. You can still proceed if you are sure the ID is correct.
              </span>
            </div>
          )}

          {/* Impact summary */}
          {composer.piece_count > 0 && (
            <Alert>
              <AlertDescription>
                This will update <strong>{composer.piece_count}</strong> canonical{' '}
                {composer.piece_count === 1 ? 'piece' : 'pieces'}.
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={promoteMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={promoteMutation.isPending || isFetching}>
              {promoteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Promote to Wikidata
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
